import moment from "moment";
import { Deserializable } from "../interfaces/deserialize";
import { Constants } from "../../features/models/constants/_constants";
import { PackModel } from "./pack.model";
import { ToolModel } from "./tool.model";

export interface Languages {
	es: string;
	en: string;
	fr: string;
	zh: string;
	it: string;
	pt: string;
	jp: string;
	tr: string;
}

export enum SaleStatus {
	pending = "pending",
	cancelled = "cancelled",
	done = "done",
}

export class SaleModel implements Deserializable {
	_id!: string;
	active!: boolean;
	amount!: number;
	client!: string;
	country?: string;
	createdAt!: string;
	erpBill!: Array<any>;
	licences!: PackModel;
	orderId!: string;
	paid!: boolean;
	paymentDetails!: {
		merchantId: string;
		sha1hash: string;
		merchantResponseUrl: string;
		payerRef: string;
	};
	paymentMethod!: string;
	product!: ToolModel;
	purchaseDate!: string;
	renew!: boolean;
	renewalDate!: string;
	sale?: {
		erpBill: {
			date: string;
			erpCode: string;
			invoiceId: string;
			numeroDoc: string;
		};
		paid: boolean;
		paymentMethod: string;
		status: string;
	};
	sort!: {
		clientName: string;
		licenceNumber: string;
		productName: string;
	}
	status!: SaleStatus;
	updatedAt!: string;

	public deserialize(obj: any) {
		Object.assign(this, obj);
		if (obj.product instanceof Object) {
			this.product = new ToolModel().deserialize(obj.product);
		}
		return this;
	}

	public checkCancelledStatus(): boolean {
		if (this.status) {
			return this.status === SaleStatus.cancelled;
		}
		return false;
	}

	public checkIfHasExpired(): boolean {
		if (this.renewalDate) {
			const currentDate = moment();
			const renewalDate = moment(this.renewalDate);
			return currentDate.isAfter(renewalDate);
		}
		return true;
	}

	public getId(): string {
		return this._id;
	}

	public getProductName(): string {
		if (this.product instanceof ToolModel) {
			return this.product.getName() ? this.product.getName() : "";
		}
		return "";
	}

	public getProductDescription(): string {
		if (this.product instanceof ToolModel) {
			return this.product.getShortDescription() ? this.product.getShortDescription() : "";
		}
		return "";
	}

	public getProductImage(): string {
		if (this.product instanceof ToolModel) {
			return this.product.getMainImage() ? this.product.getMainImage() : "";
		}
		return "";
	}

	public getProductManuals(): Array<any> {
		if (this.product instanceof ToolModel) {
			return this.product.getManuals() ? this.product.getManuals() : [];
		}
		return [];
	}

	public getProductQuotation(): Array<any> {
		if (this.product instanceof ToolModel) {
			return this.product.getQuotation() ? this.product.getQuotation() : [];
		}

		return [];
	}

	public getStatus(): boolean {
		return this.active;
	}

	public getLicences(): string {
		return this.licences.licenceNumber ? this.licences.licenceNumber : "0";
	}

	public getPrice(): number {
		return this.amount ? this.amount / 100 : 0;
	}

	public getParsedPaymentMethod(): string {
		if (this.paymentMethod && this.paymentMethod.toUpperCase() in Constants.PAYMENT_OPTIONS_NAMES) {
			return Constants.PAYMENT_OPTIONS.filter((element) => {
				return element.id === this.paymentMethod;
			})[0].title;
		}
		return "---";
	}

	public getRenewStatus(): boolean {
		return this.renew;
	}

	public getLastPayment(): string {
		if (this.purchaseDate) {
			return moment(this.purchaseDate).format("LL");
		}
		return "---";
	}

	public getNextPayment(): string {
		if (this.renewalDate) {
			return moment(this.renewalDate).format("LL");
		}
		return "---";
	}

	public getBill(): string {
		if (this.erpBill && this.erpBill.length > 0) {
			return this.erpBill[0].numeroDoc;
		} else {
			return "";
		}
	}

	public extractIds() {
		let dto: any = { ...this };
		if (this.product instanceof ToolModel) {
			dto.product = this.product._id;
		}

		return dto;
	}
}
